import { useDropzone } from 'react-dropzone';
import { FileUploadList } from './FileUploadList';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../store';
import { addFile } from '../../../store/slices/fileUploadSlice';
import { useEffect, useState } from 'react';
import { PrimaryButton, WhiteButton } from '../Button/Buttons';
import { FileUploadProps } from './FileUpload.types';
import { Toast } from '../Toast/Toast';
import { AddDocumentIcon } from '../../../icons/SvgIcons';
import {
  ACCEPT,
  MAX_DOCUMENT_FILE_SIZE_BYTES,
  tooLargeMessage,
  unsupportedTypeMessage,
  validateDocumentFile
} from '../../../utils/fileUploadValidation';

export function FileUpload({
  title,
  description,
  icon,
  classNames,
  suportedFiles,
  maxFiles,
  minFiles,
  maxFileSize,
  handleCancel,
  handleUpload,
  supportedFileText,
  uploading = false,
  uploadDisabled = false,
  buttonText = 'Upload'
}: FileUploadProps) {
  const files = useSelector((state: RootState) => state.fileUpload);
  const dispatch = useDispatch();

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const acceptedFilesObject = Object.fromEntries(
    suportedFiles.acceptFile.map((file) => [file, ACCEPT[file] ?? []])
  );

  const resolvedMaxFileSize = maxFileSize ?? MAX_DOCUMENT_FILE_SIZE_BYTES;

  const disableUpload = files.length >= maxFiles;

  const disableUploadButton =
    files.length <= 0 || files.length < minFiles! || uploading || uploadDisabled;

  const primaryButtonText = buttonText
    ? buttonText
    : uploading
      ? 'Uploading...'
      : 'Upload';

  const resolvedSupportedFileText =
    supportedFileText ??
    suportedFiles.fileName.map((file, index) =>
      suportedFiles.fileName.length - 1 === index ? file : `${file}, `
    );

  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptedFilesObject,
    maxSize: resolvedMaxFileSize,
    disabled: disableUpload,
    onDrop: (acceptedFiles, fileRejections) => {
      // Defense-in-depth: re-validate dropzone-accepted files against the
      // shared backend contract, moving any that fail into the rejected set.
      const validFiles: File[] = [];
      let guardRejectedOversized = false;
      let guardRejectedCount = 0;

      acceptedFiles.forEach((file) => {
        const validation = validateDocumentFile(file);
        if (validation.valid) {
          validFiles.push(file);
        } else {
          guardRejectedCount += 1;
          if (validation.reason === 'too-large') guardRejectedOversized = true;
        }
      });

      const totalRejections = fileRejections.length + guardRejectedCount;

      if (totalRejections > 0) {
        const hasOversizedFile =
          guardRejectedOversized ||
          fileRejections.some((rejection) =>
            rejection.errors.some((error) => error.code === 'file-too-large')
          );

        if (hasOversizedFile) {
          setToastMessage(tooLargeMessage());
        } else if (totalRejections === 1) {
          setToastMessage(unsupportedTypeMessage());
        } else {
          setToastMessage(
            `${totalRejections} files were rejected. ${unsupportedTypeMessage()}`
          );
        }
        setShowToast(true);
      }

      const totalFilesCount = files.length + validFiles.length;

      if (totalFilesCount > maxFiles) {
        const allowedCount = maxFiles - files.length;

        if (allowedCount <= 0) {
          setToastMessage(
            `Only ${maxFiles} file${maxFiles > 1 ? 's are' : ' is'} allowed.`
          );
          setShowToast(true);
          return;
        }

        const allowedFiles = validFiles.slice(0, allowedCount);

        allowedFiles.forEach((file) => {
          dispatch(
            addFile({
              id: crypto.randomUUID(),
              file: file
            })
          );
        });

        setToastMessage(
          `Only ${maxFiles} file${maxFiles > 1 ? 's are' : ' is'} allowed.`
        );
        setShowToast(true);
        return;
      }

      validFiles.forEach((file) => {
        dispatch(
          addFile({
            id: crypto.randomUUID(),
            file: file
          })
        );
      });
    }
  });

  useEffect(() => {
    const handlePreventDrop = (e: DragEvent) => {
      if (disableUpload) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener('drop', handlePreventDrop);
    document.addEventListener('dragover', handlePreventDrop);
    return () => {
      document.removeEventListener('drop', handlePreventDrop);
      document.removeEventListener('dragover', handlePreventDrop);
    };
  }, [files.length, maxFiles]);

  return (
    <>
      <div className={`flex flex-col items-center justify-center${classNames}`}>
        <div className="w-full flex flex-col gap-2 items-start">
          {title && (
            <p className="text-sm font-medium text-gray-800">
              Upload Files (Optional)
            </p>
          )}
          {description && (
            <p className="text-sm text-gray-500 text-left">
              Your uploaded documents are stored securely and serve as
              supporting evidence for audits or regulatory reviews
            </p>
          )}
        </div>
        <div
          {...getRootProps()}
          className={`flex flex-col items-center justify-center w-full h-[140px] border-2 border-dashed rounded-md border-gray-200 ${disableUpload ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center justify-center gap-1 pt-[22px] pb-[26px]">
            {AddDocumentIcon(disableUpload)}
            <p
              className={`text-sm font-medium ${
                disableUpload ? 'text-gray-200' : 'text-gray-800'
              }`}
            >
              <span
                className={`text-xs ${
                  disableUpload ? 'text-gray-200' : 'text-purple-800'
                }`}
              >
                Upload a file
              </span>{' '}
              or drag and drop
            </p>
            <p
              className={`text-xs ${
                disableUpload ? 'text-gray-200' : ' text-gray-550'
              }`}
            >
              {resolvedSupportedFileText}
            </p>
          </div>
        </div>
        {files.length > 0 && <FileUploadList files={files} />}
        <div className="flex items-center justify-end w-full h-[38px] gap-3 mt-6">
          <WhiteButton onClick={handleCancel} disabled={uploading}>
            Cancel
          </WhiteButton>
          <PrimaryButton onClick={handleUpload} disabled={disableUploadButton}>
            {primaryButtonText}
          </PrimaryButton>
        </div>
      </div>
      <Toast
        type="error"
        setShowToast={setShowToast}
        showToast={showToast}
        text={toastMessage}
      />
    </>
  );
}
